<script lang="ts">import { browser } from "$app/environment";
import { getCategories } from "./data/categories";
import { recentlyPlayedGames } from "@infinitro/catalog-ex";
import { cfgEventThemeCdnPath, FeatureSet, isEventAssetEnabled, isEventThemeActive, ReleaseStage, stateFeatureSet, stateHasSports, stateTheme, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isFullScreenGame, isGamePlaying, isIOS, isMobile, locale, loggedIn, ModalDialog, refreshRaces, selectedItemMobileMenu, stateOpenModal, stateRaces, stateShowSidebarMobile } from "@infinitro/global";
import { onMount, tick } from "svelte";
import { derived } from "svelte/store";
import Banner from "./parts/banner.svelte";
import CasinoDropdown from "./parts/casino-dropdown.svelte";
import CryptoPriceDropdown from "./parts/crypto-price-dropdown.svelte";
import LanguageDropdown from "./parts/language-dropdown.svelte";
import LuckyDip from "./parts/lucky-dip.svelte";
import RecentlyPlayedItem from "./parts/recently-played-item.svelte";
import SearchInput from "./parts/search-input.svelte";
import SidebarItem from "./parts/sidebar-item.svelte";
import SportsDropdown from "./parts/sports-dropdown.svelte";
import Support from "./parts/support.svelte";
import UpdownGameDropdown from "./parts/updown-game-dropdown.svelte";
import { languageIsExpanded, menuAutoCollapse, menuAutoCollapsed, menuMobileOpen } from "./state";
export let isCollapsed = false;
export let useParentState = false;
export let isOpen = false;
export let withoutTopBorder = false;
// Flag to control image divider (only show when event theme is active)
let useImageDivider = isEventThemeActive();
let sidebarEndElement = null;
let sidebarElement = null;
let isMenuAutoCollapse = false;
let isMenuCollapsed = false;
let isMenuMobileOpen = false;
let hasInitializedMobileMenuOpenState = false;
let hasOpenedMobileSidebar = false;
let isLanguageExpanded = false;
let lastParentCollapsedState;
let expandMenuApplyFrame;
let languageExpandSettleTimeout;
let scrollContainer = null;
let isLoggedIn = undefined;
let sectionTitleElement;
let sectionTitleFontSize = 12;
let touchStartY = 0;
const keepLanguageDropdownFullyVisible = (behavior = "smooth") => {
	if (!sidebarElement) return;
	const expandedLanguageMenu = sidebarElement.querySelector(".languages .dropdown-menu.expanded");
	if (!expandedLanguageMenu) return;
	const sidebarRect = sidebarElement.getBoundingClientRect();
	const menuRect = expandedLanguageMenu.getBoundingClientRect();
	const minimumBottomGap = 28;
	const currentBottomGap = sidebarRect.bottom - menuRect.bottom;
	const neededScroll = minimumBottomGap - currentBottomGap;
	if (neededScroll > 0) {
		sidebarElement.scrollTo({
			top: sidebarElement.scrollTop + neededScroll,
			behavior
		});
	}
};
loggedIn.subscribe((value) => {
	isLoggedIn = value;
});
// Adjust font size based on longest word length when collapsed
const adjustFontSize = () => {
	if (!sectionTitleElement) return;
	// Reset to normal if not collapsed
	if (!isMenuCollapsed) {
		sectionTitleFontSize = 12;
		return;
	}
	setTimeout(() => {
		// Use actual sidebar width (74px) minus padding and margin
		const containerWidth = 50;
		const text = sectionTitleElement.textContent || "";
		const words = text.split(/\s+/).filter((w) => w.length > 0);
		if (words.length === 0) return;
		// Create temp element to measure word widths at 14px
		const temp = document.createElement("span");
		temp.style.cssText = `
				font-family: var(--font-main);
				font-size: 14px;
				font-weight: 100;
				visibility: hidden;
				position: absolute;
				white-space: nowrap;
			`;
		document.body.appendChild(temp);
		// Find longest word width
		let maxWordWidth = 0;
		words.forEach((word) => {
			temp.textContent = word;
			maxWordWidth = Math.max(maxWordWidth, temp.offsetWidth);
		});
		document.body.removeChild(temp);
		// Scale down with aggressive safety margin to ensure fit when collapsed
		sectionTitleFontSize = Math.max(5, Math.floor(containerWidth / maxWordWidth * 12 * .75) + 1);
	}, 50);
};
locale.subscribe(() => {
	if (sectionTitleElement) adjustFontSize();
});
menuAutoCollapsed.subscribe((value) => {
	isMenuCollapsed = value;
	adjustFontSize();
});
$: if (isMenuCollapsed !== undefined) {
	adjustFontSize();
}
const openSearchModal = () => {
	stateOpenModal.set(ModalDialog.Search);
	stateShowSidebarMobile.set(false);
};
onMount(() => {
	const blockIOSRubberBand = (event) => {
		if (!$isIOS || !sidebarElement) return;
		const touch = event.touches[0];
		if (!touch) return;
		const currentY = touch.clientY;
		const isPullingDown = currentY > touchStartY;
		const isPushingUp = currentY < touchStartY;
		const atTop = sidebarElement.scrollTop <= 0;
		const atBottom = sidebarElement.scrollTop + sidebarElement.clientHeight >= sidebarElement.scrollHeight - 1;
		if (atTop && isPullingDown || atBottom && isPushingUp) {
			event.preventDefault();
		}
	};
	const saveTouchStart = (event) => {
		const touch = event.touches[0];
		if (!touch) return;
		touchStartY = touch.clientY;
	};
	const updateSidebarLayout = () => {
		window.dispatchEvent(new Event("resize"));
	};
	refreshRaces();
	tick().then(() => {
		setTimeout(() => {
			if (sectionTitleElement) {
				adjustFontSize();
			}
		}, 1e3);
	});
	// Set event theme backgrounds if active
	if (sidebarElement) {
		if (isEventAssetEnabled("sidebarBackground")) {
			const sidebarBgPath = cfgEventThemeCdnPath("sidebarbg.png");
			if (sidebarBgPath) {
				sidebarElement.style.setProperty("--event-sidebar-bg", `url('${sidebarBgPath}')`);
			}
		}
		if (isEventAssetEnabled("divider")) {
			const dividerPath = cfgEventThemeCdnPath("divider.png");
			if (dividerPath) {
				sidebarElement.style.setProperty("--event-divider-bg", `url('${dividerPath}')`);
			}
		}
	}
	const handleClickOutside = (event) => {
		if (isMenuMobileOpen && $isMobile && sidebarElement) {
			const sidebarRect = sidebarElement.getBoundingClientRect();
			const clickX = event.clientX;
			const clickY = event.clientY;
			const clickedOutsideSidebar = clickX > sidebarRect.right || clickX < sidebarRect.left;
			const clickedAtSidesOnly = clickY > sidebarRect.top && clickY < sidebarRect.bottom;
			if (clickedOutsideSidebar && clickedAtSidesOnly) {
				isMenuMobileOpen = false;
			}
		}
	};
	document.addEventListener("click", handleClickOutside);
	sidebarElement?.addEventListener("touchstart", saveTouchStart, { passive: true });
	sidebarElement?.addEventListener("touchmove", blockIOSRubberBand, { passive: false });
	const toggleBodyScroll = () => {
		if (isMenuMobileOpen) {
			document.body.classList.add("overflow-hidden");
		} else {
			document.body.classList.remove("overflow-hidden");
		}
	};
	const menuMobileOpenUnsub = menuMobileOpen.subscribe((value) => {
		const wasMenuMobileOpen = isMenuMobileOpen;
		isMenuMobileOpen = value;
		// Ignore the first subscription emission to avoid inheriting stale store state
		// (which can trigger a ghost hide animation on game start in mobile).
		if (!hasInitializedMobileMenuOpenState) {
			hasInitializedMobileMenuOpenState = true;
		} else if (value && !wasMenuMobileOpen && $selectedItemMobileMenu === "menu") {
			// Only enable hide animation after an explicit user open from mobile "Menu".
			hasOpenedMobileSidebar = true;
		}
		if (value && $isMobile && scrollContainer) {
			scrollContainer.scrollTop = 0;
			window.scrollTo(0, 0);
			document.documentElement.scrollTop = 0;
			document.body.scrollTop = 0;
		}
		toggleBodyScroll();
		updateSidebarLayout();
		requestAnimationFrame(updateSidebarLayout);
		setTimeout(updateSidebarLayout, 150);
	});
	return () => {
		document.removeEventListener("click", handleClickOutside);
		sidebarElement?.removeEventListener("touchstart", saveTouchStart);
		sidebarElement?.removeEventListener("touchmove", blockIOSRubberBand);
		document.body.classList.remove("overflow-hidden");
		if (expandMenuApplyFrame !== undefined) {
			cancelAnimationFrame(expandMenuApplyFrame);
			expandMenuApplyFrame = undefined;
		}
		if (languageExpandSettleTimeout !== undefined) {
			clearTimeout(languageExpandSettleTimeout);
			languageExpandSettleTimeout = undefined;
		}
		menuMobileOpenUnsub();
	};
});
menuAutoCollapse.subscribe((value) => isMenuAutoCollapse = value);
languageIsExpanded.subscribe(async (value) => {
	isLanguageExpanded = value;
	if (value) {
		await tick();
		if (sidebarElement && sidebarEndElement) {
			sidebarElement.scrollTo({
				top: sidebarEndElement.offsetTop,
				behavior: "smooth"
			});
			keepLanguageDropdownFullyVisible("smooth");
			if (languageExpandSettleTimeout !== undefined) {
				clearTimeout(languageExpandSettleTimeout);
			}
			// Re-check after the dropdown expansion animation completes.
			languageExpandSettleTimeout = window.setTimeout(() => {
				keepLanguageDropdownFullyVisible("smooth");
			}, 320);
		}
	}
});
$: {
	menuMobileOpen.set(isOpen);
}
$: {
	isOpen = isMenuMobileOpen;
}
$: if (browser && useParentState) {
	if (lastParentCollapsedState !== isCollapsed) {
		lastParentCollapsedState = isCollapsed;
		if (expandMenuApplyFrame !== undefined) {
			cancelAnimationFrame(expandMenuApplyFrame);
			expandMenuApplyFrame = undefined;
		}
		// Collapse immediately; defer expand a frame so outer layout width vars can paint first.
		if (isCollapsed) {
			menuAutoCollapsed.set(true);
		} else {
			expandMenuApplyFrame = requestAnimationFrame(() => {
				menuAutoCollapsed.set(false);
				expandMenuApplyFrame = undefined;
			});
		}
	}
}
$: {
	isLoggedIn;
}
const sidebarCategories = derived([
	loggedIn,
	stateFeatureSet,
	stateTheme,
	stateRaces,
	isMobile,
	stateUi
], ([$loggedIn, $stateFeatureSet, $stateTheme, $stateRaces, $isMobile, $stateUi], set) => {
	const cat = getCategories($loggedIn, $stateFeatureSet, $stateTheme, $stateRaces, $isMobile, $stateUi.sidebar);
	set(cat);
}, []);
$: useAlternative = $stateUi.sidebar.sidebarStyle === "alternative";
$: useAlternative2 = $stateUi.sidebar.sidebarStyle === "alternative2";
$: useAlternative4 = $stateUi.sidebar.sidebarStyle === "alternative4";
$: useDefault = $stateUi.sidebar.sidebarStyle === "default";
$: supportFullWidthMobile = $stateUi.sidebar.supportFullWidthMobile ?? false;
$: hideSidebarOnMobileFullscreen = $stateUi.games.showLogoWhenPlayingDesktop && $isMobile && $isFullScreenGame && $isGamePlaying && $stateUi.games.showNavigationOnMobile && $selectedItemMobileMenu === "chat";
$: if (hideSidebarOnMobileFullscreen) {
	hasOpenedMobileSidebar = false;
}
$: canShowInMobileGame = $isMobile && $isFullScreenGame && $isGamePlaying && $stateUi.games.showNavigationOnMobile;
</script>

{#if !$isFullScreenGame || canShowInMobileGame}
	<aside
		aria-label="Sidebar"
		bind:this={sidebarElement}
		class:mobile-game-nav={canShowInMobileGame}
		class:hidden-while-mobile-fullscreen={hideSidebarOnMobileFullscreen}
		class:mobile-hide-animated={hasOpenedMobileSidebar}
		class="{isMenuCollapsed ? 'collapsed' : 'open'} {$isMobile && isMenuMobileOpen
			? 'mobile-visible'
			: 'mobile-hidden'} {withoutTopBorder ? 'no-top-border' : ''}"
		id="sidebar"
		class:alternative-2-sidebar={useAlternative2}
		class:event-theme={isEventThemeActive()}
		class:alternative-4-aside={useAlternative4}
	>
		<div class="scroll-container" bind:this={scrollContainer}>
			<div
				class="top-items {isMenuCollapsed && !$isMobile ? 'top-items-collapsed' : ''}"
				class:top-items-alternative-4={useAlternative4}
			>
				{#if $stateUi.layout.showCryptoPriceDropdown}
					<div class="item" class:alternative-item={useAlternative || useAlternative4}>
						<CryptoPriceDropdown />
					</div>
				{/if}
				{#if $stateUi.sidebar.showLuckyDip}
					<div class="item" class:alternative-item={useAlternative || useAlternative4}>
						<LuckyDip />
					</div>
				{/if}

				<div class="item" class:alternative-item={useAlternative || useAlternative4}>
					<SearchInput {openSearchModal} />
				</div>

				{#if $stateUi.sidebar.showBanner}
					<div class="item" class:alternative-item={useAlternative || useAlternative4}>
						<Banner />
					</div>
				{/if}
				{#if $stateUi.layout.showUpdownGameDropdown}
					<div class="item" class:alternative-item={useAlternative || useAlternative4}>
						<UpdownGameDropdown />
					</div>
				{/if}
				<div class="item" class:alternative-item={useAlternative || useAlternative4}>
					<CasinoDropdown />
				</div>

				{#if $stateHasSports}
					<div class="item" class:alternative-item={useAlternative || useAlternative4}>
						<SportsDropdown />
					</div>
				{/if}
				<div class={`item-divider ${isMenuCollapsed ? 'item-divider-collapsed' : ''}`}>
					<div class="divider" class:divider-image={useImageDivider}></div>
				</div>
				<div class="sidebar-items">
					<div
						class="item item-opacity"
						class:alternative-item={useAlternative || useAlternative4}
						class:alternative-4-item-group={useAlternative4}
					>
						{#each $sidebarCategories as category (category.href)}
							<SidebarItem
								bind:isMenuCollapsed
								paddingLeft={'12px'}
								{category}
								itemIdPrefix="sidebar"
							/>
						{/each}
					</div>
				</div>
			</div>
			{#if Array.isArray($recentlyPlayedGames) && $recentlyPlayedGames.length > 0}
				<div class="recently-played">
					{#if !isMenuCollapsed}
						<div
							class="section-title"
							bind:this={sectionTitleElement}
							style="font-size: {sectionTitleFontSize}px;"
						>
							{$t(Verbiage.recently_played)}
						</div>
					{/if}
					<div class="sidebar-items">
						{#each $recentlyPlayedGames.slice(0, 3) as game}
							<RecentlyPlayedItem {game} />
						{/each}
					</div>
				</div>
			{/if}
			<div
				class="bottom-items {isMenuCollapsed && !$isMobile ? 'bottom-items-collapsed' : ''}"
				class:bottom-items-full-support={supportFullWidthMobile}
				class:bottom-items-alternative-4={useAlternative4}
			>
				<div
					class="item support"
					class:alternative-item={useAlternative || useAlternative4}
					class:support-full-mobile={supportFullWidthMobile}
				>
					<Support />
				</div>
				<div class="item languages" class:alternative-item={useAlternative || useAlternative4}>
					<LanguageDropdown />
				</div>
				<div id="sidebar-end" bind:this={sidebarEndElement} />
			</div>
		</div>
	</aside>
{/if}

<style>
	@keyframes slideUpMobilePanel {
		from {
			transform: translate3d(0, 100%, 0);
		}
		to {
			transform: translate3d(0, 0, 0);
		}
	}

	@keyframes slideDownMobilePanel {
		from {
			opacity: 1;
			transform: translate3d(0, 0, 0);
		}
		99% {
			opacity: 1;
		}
		to {
			opacity: 0;
			transform: translate3d(0, calc(100% + 60px + env(safe-area-inset-bottom, 0px)), 0);
		}
	}

	@keyframes sidebarContentCollapse {
		0% {
			opacity: 0;
			transform: translateX(-20px);
		}
		76% {
			opacity: 0;
			transform: translateX(-20px);
		}
		90% {
			opacity: 0.55;
			transform: translateX(-10px);
		}
		100% {
			opacity: 1;
			transform: translateX(0);
		}
	}

	@keyframes sidebarContentExpand {
		0% {
			opacity: 0;
			transform: translateX(-24px);
		}
		3% {
			opacity: 0;
			transform: translateX(-24px);
		}
		76% {
			opacity: 0;
			transform: translateX(-24px);
		}
		90% {
			opacity: 0.55;
			transform: translateX(-10px);
		}
		100% {
			opacity: 1;
			transform: translateX(0);
		}
	}

	aside {
		position: fixed;
		display: flex;
		flex-shrink: 0;
		flex-direction: column;
		justify-content: space-between;
		border-width: 1px;
		border-left-width: 0px;
		border-bottom-width: 0px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-700);
		--tw-bg-opacity: 1;
		padding-left: 0.875rem;
		padding-right: 0.875rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		--tw-backdrop-blur: blur(50px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
}

	@media (min-width: 1024px) {
		aside {
				visibility: visible;
		}
}

	aside {
		transition:
			width 0.25s cubic-bezier(0.4, 0, 0.2, 1),
			padding 0.25s cubic-bezier(0.4, 0, 0.2, 1);
		overflow-y: auto;
		overflow-x: hidden;
		-webkit-overflow-scrolling: touch;
		height: calc(var(--vh, 1vh) * 100 - 72px);
		height: calc(var(--app-vh, 100dvh) - 72px - env(safe-area-inset-bottom, 0px));
		height: calc(100dvh - 72px - env(safe-area-inset-bottom, 0px));
		max-height: calc(var(--vh, 1vh) * 100 - 72px);
		max-height: calc(var(--app-vh, 100dvh) - 72px - env(safe-area-inset-bottom, 0px));
		max-height: calc(100dvh - 72px - env(safe-area-inset-bottom, 0px));
		animation: none;
		position: relative;
		@media (max-width: 1023px) {
			top: calc(72px + env(safe-area-inset-top, 0px));
			bottom: 0;
			height: auto;
			max-height: none;
		}
	}

	aside.event-theme::before {
		content: '';
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background-image: var(--event-sidebar-bg);
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		opacity: 0.05;
		z-index: -1;
		pointer-events: none;
	}

	aside::-webkit-scrollbar {
		width: 0px;
		background-color: transparent;
}

	aside::-webkit-scrollbar-track {
		background-color: transparent;
}

	aside.open {
		width: 100vw;
		max-width: 100vw;
}

	@media (min-width: 768px) {
		aside.open {
				width: 240px;
		}
}

	@media (min-width: 1024px) {
		aside.open {
				max-width: 240px;
		}
}

	aside.collapsed {
		width: 100%;
}

	@media (min-width: 640px) {
		aside.collapsed {
				width: 74px;
		}
}

	aside.collapsed::-webkit-scrollbar {
		height: 0px;
		width: 0px;
		padding: 0px;
}

	aside.collapsed::-webkit-scrollbar-thumb {
		background-color: transparent;
}

	aside.no-top-border {
		border-top-width: 0px;
}

	aside.hidden-while-mobile-fullscreen {
		display: none !important;
	}

	aside.alternative-4-aside {
		background-color: var(--component-layout-side-bar-bg);
}

	.alternative-2-sidebar {
		border-style: none;
		background-color: transparent;
		--tw-backdrop-blur: blur(30px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		background: linear-gradient(
			to bottom,
			rgba(var(--color-tertiary-100-rgb) / 0.4),
			rgba(var(--color-background-900-rgb) / 0.4)
		);
}

	div.scroll-container {
		display: flex;
		height: -moz-fit-content;
		height: fit-content;
		min-height: 100%;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
		overflow: visible;
		opacity: 1;
		transform: translateX(0);
		transition:
			opacity 0.2s ease,
			transform 0.2s cubic-bezier(0, 0, 0.2, 1);
}

	div.scroll-container::-webkit-scrollbar {
		height: 0px;
		width: 0px;
		background-color: transparent;
		padding: 0px;
}

	div.scroll-container::-webkit-scrollbar-track {
		background-color: transparent;
}

	div.backdrop {
		position: fixed;
		inset: 0px;
		z-index: 10;
		background-color: rgb(17 24 39 / 0.5);
}

	@media (min-width: 1024px) {
		div.backdrop {
				display: none;
		}
}

	@media (prefers-color-scheme: dark) {
		div.backdrop {
				background-color: rgb(17 24 39 / 0.9);
		}
}

	div.top-items {
		display: flex;
		width: 100%;
		flex-grow: 1;
		flex-direction: column;
}

	div.top-items-alternative-4 {
		gap: 0.5rem;
}

	div.top-items-alternative-4 > div.item,
	div.top-items-alternative-4 > div.item-divider {
		margin-top: 0px;
		margin-bottom: 0px;
}

	div.item.alternative-4-item-group {
		gap: 0.5rem;
}

	div.bottom-items-alternative-4 {
		gap: 0.5rem;
}

	div.bottom-items-alternative-4 > div.item {
		margin-top: 0px;
		margin-bottom: 0px;
}

	div.top-items-collapsed .item,
	div.bottom-items-collapsed .item {
		width: 100%;
}

	div.bottom-items {
		margin-top: auto;
		display: flex;
		width: 100%;
		flex-shrink: 0;
		flex-direction: row-reverse;
		justify-content: flex-end;
}

	@media (min-width: 1024px) {
		div.bottom-items {
				flex-direction: column;
		}
}

	div.bottom-items-full-support {
		flex-direction: column;
}

	div.item {
		position: relative;
		margin-top: 8px;
		margin-bottom: 8px;
		display: flex;
		min-height: 40px;
		flex-direction: column;
		background-color: transparent;
		color: var(--color-text-300);
		transition:
			opacity 0.1s ease,
			transform 0.1s ease;
		overflow: visible;
}

	div.alternative-item {
		color: var(--color-text-100);
}

	.support {
		width: 40px;
}

	@media (min-width: 1024px) {
		.support {
				width: 100%;
		}
}

	.support-full-mobile {
		width: 100%;
}

	.languages {
		margin-right: 0.5rem;
		width: 100%;
}

	@media (min-width: 1024px) {
		.languages {
				margin-right: 0px;
		}
}

	div.sidebar-subtitle {
		height: 20px;
		width: 100%;
		overflow: hidden;
		padding-left: 6px;
		transition: opacity 1s ease;
		transition-delay: 0s;
}

	div.sidebar-subtitle-collapsed {
		height: 20px;
		opacity: 0;
		transition: opacity 0s ease;
		transition-delay: 0s;
}

	div.item-divider {
		margin-bottom: 0.375rem;
		margin-top: 0.75rem;
		width: 100%;
}

	div.divider {
		height: 1px;
		width: 100%;
		background-color: var(--color-neutral-900);
}

	div.divider-image {
		background-color: transparent;
		background-image: var(--event-divider-bg);
		background-size: contain;
		background-position: center;
		background-repeat: no-repeat;
		height: 20px;
		opacity: 1;
	}

	.section-title {
		margin-bottom: 4px;
		overflow: hidden;
		text-align: left;
		--tw-text-opacity: 1;
		color: rgb(156 163 175 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		font-size: clamp(6px, 2.5vw, 12px);
		word-break: keep-all;
		white-space: normal;
		line-height: 1.2;
		font-weight: 400;
}

	div.language-support {
		display: flex;
		width: 100%;
		justify-content: space-between;
		gap: 0.5rem;
		transition: flex-direction 0.1s ease;
}

	div.language-support-collapsed {
		flex-direction: column;
		align-items: center;
		gap: 0.5rem;
}

	.toggle-button {
		position: fixed;
		top: 6rem;
		left: 14.95rem;
		height: 2rem;
		width: 2.25rem;
		cursor: pointer;
		border-top-right-radius: 0.25rem;
		border-bottom-right-radius: 0.25rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-100);
		--tw-bg-opacity: 1;
		--tw-backdrop-blur: blur(1px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		display: none;
		align-items: center;
		justify-content: center;
}

	@media (min-width: 1024px) {
		.toggle-button {
				display: flex;
		}
}

	.toggle-button {
		transition: left 0.25s cubic-bezier(0.4, 0, 0.2, 1);
	}

	.toggle-button-collapsed {
		left: 73px;
}

	.collapsed-arrow {
		--tw-rotate: 180deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		transition: transform 0.1s ease;
}

	@media (max-width: 1023px) {
		aside {
			touch-action: pan-y;
		}

		aside.mobile-game-nav {
			position: fixed;
			left: 0;
			top: calc(72px + env(safe-area-inset-top, 0px));
			bottom: calc(60px + env(safe-area-inset-bottom, 0px));
			height: auto;
			max-height: none;
		}

		/* Full-width mobile drawer animation */
		@media (max-width: 767px) {
			.mobile-visible {
						display: flex;
				transition: transform 0.85s cubic-bezier(0.22, 1, 0.36, 1);
				animation: slideUpMobilePanel 0.85s cubic-bezier(0.22, 1, 0.36, 1);
				animation-fill-mode: both;
				opacity: 1;
				transform: translate3d(0, 0, 0);
				will-change: transform;
				backface-visibility: hidden;
				position: fixed;
				top: calc(72px + env(safe-area-inset-top, 0px));
				bottom: calc(60px + env(safe-area-inset-bottom, 0px));
				height: auto;
				max-height: none;
				}

			.mobile-hidden {
				display: none;
				pointer-events: none;
			}

			.mobile-hidden.mobile-hide-animated {
						display: flex;
				transition: transform 1.05s cubic-bezier(0.22, 1, 0.36, 1);
				animation: slideDownMobilePanel 1.05s cubic-bezier(0.22, 1, 0.36, 1);
				animation-fill-mode: both;
				opacity: 1;
				transform: translate3d(0, 100%, 0);
				will-change: transform;
				backface-visibility: hidden;
				position: fixed;
				top: calc(72px + env(safe-area-inset-top, 0px));
				bottom: calc(60px + env(safe-area-inset-bottom, 0px));
				height: auto;
				max-height: none;
				pointer-events: none;
				}
		}

		/* Tablet/mobile-side panel: show/hide without slide-from-bottom animation */
		@media (min-width: 768px) {
			.mobile-visible {
						display: flex;
				animation: none;
				opacity: 1;
				transform: none;
				position: fixed;
				top: calc(72px + env(safe-area-inset-top, 0px));
				bottom: calc(60px + env(safe-area-inset-bottom, 0px));
				height: auto;
				max-height: none;
				}

			.mobile-hidden {
				display: none;
				animation: none;
				pointer-events: none;
			}
		}
	}

	@media (min-width: 1024px) {
		aside {
			will-change: width;
		}

		aside.collapsed div.scroll-container {
			animation: sidebarContentCollapse 0.33s cubic-bezier(0.16, 1, 0.3, 1);
			animation-fill-mode: both;
			transition: none;
			will-change: opacity, transform;
		}

		aside.open div.scroll-container {
			animation: sidebarContentExpand 0.33s cubic-bezier(0.16, 1, 0.3, 1);
			animation-fill-mode: both;
			transition: none;
			will-change: opacity, transform;
		}

		.mobile-visible,
		.mobile-hidden {
				display: flex;
			opacity: 1;
			transform: none;
			position: relative;
		}
	}
</style>
